/**
 * 查找替换：编辑器搜索高亮 + 文本偏移 → 文档位置映射 + 替换事务。
 *
 * 设计要点：
 * - 匹配基于 `editor.getText()` 的**纯文本偏移**（块之间为 BLOCK_SEPARATOR），
 *   与 `utils/findReplace.ts` 的纯函数保持同一坐标系；
 * - 高亮用 ProseMirror Decoration 实现，不改动文档内容；
 * - 替换直接派发事务，因此**可撤销**（Ctrl+Z 能回退）。
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/react';
import type { Node as PMNode } from '@tiptap/pm/model';
import { findMatches } from '../../utils/findReplace';

/**
 * `editor.getText()` 在相邻块之间插入的分隔符。
 *
 * 实测（TipTap 3）是**双换行** `\n\n`，不是单个 `\n`。
 * 必须与真实文本严格一致：曾按单个 `\n` 估算，
 * 使第二个段落起的所有偏移整体差 1 —— 表现为第一段替换正常，
 * 后续段落的替换落到字符中间（「林渊看见林渊」→「林李默见林李默」）。
 */
export const BLOCK_SEPARATOR = '\n\n';

export interface SearchHighlightState {
  query: string;
  matches: Array<{ start: number; length: number }>;
  activeIndex: number;
  caseSensitive: boolean;
  regex: boolean;
}

export const SEARCH_HIGHLIGHT_KEY = 'searchHighlight';

const pluginKey = new PluginKey<SearchHighlightState>(SEARCH_HIGHLIGHT_KEY);

/** 文本偏移区间 → 文档位置区间的映射表（构建一次，供多次换算） */
interface OffsetMap {
  /** 每个文本段的起始偏移、起始文档位置、长度 */
  segments: Array<{ offset: number; pos: number; length: number }>;
}

/** 建立 getText() 偏移与文档位置的对应关系（导出以便单测） */
export function buildOffsetMap(doc: PMNode): OffsetMap {
  const segments: OffsetMap['segments'] = [];
  let offset = 0;
  let seenBlock = false;

  doc.descendants((node, pos) => {
    if (node.isText) {
      const text = node.text ?? '';
      segments.push({ offset, pos, length: text.length });
      offset += text.length;
      return false;
    }
    if (node.isBlock) {
      // getText() 在相邻块之间插入块分隔符（实测 TipTap 3 为 \n\n，不是单个 \n）。
      // 分隔符长度必须与真实文本严格一致：曾按单个 \n 估算，
      // 使第二个段落起的所有文本偏移整体差 1，替换落到字符中间。
      // 根 doc 的 isBlock 为 false，因此不会被误计为块。
      if (seenBlock) offset += BLOCK_SEPARATOR.length;
      seenBlock = true;
    }
    return true;
  });

  return { segments };
}

/** 把 [start, start+length) 的文本偏移换算为文档位置区间；跨越块边界时返回 null（导出以便单测） */
export function textRangeToDocRange(map: OffsetMap, start: number, length: number): { from: number; to: number } | null {
  const end = start + length;
  if (length <= 0) return null;

  // 起止必须落在**同一个**文本段内：否则区间横跨块边界（含换行），
  // 无法用一次行内替换表达。旧实现只检查"各自落在某个段内"，
  // 于是跨段区间会被错误地映射成一个把中间内容一并吞掉的范围。
  for (const seg of map.segments) {
    const segStart = seg.offset;
    const segEnd = seg.offset + seg.length;
    if (start < segStart || start > segEnd) continue;
    if (end > segEnd) return null; // 尾部越出本段 → 跨段
    return { from: seg.pos + (start - segStart), to: seg.pos + (end - segStart) };
  }
  return null;
}

/** 根据搜索状态构造装饰集 */
function buildDecorations(doc: PMNode, state: SearchHighlightState | undefined): DecorationSet {
  if (!state || !state.query || state.matches.length === 0) return DecorationSet.empty;
  const map = buildOffsetMap(doc);
  const decorations: Decoration[] = [];

  state.matches.forEach((m, index) => {
    const range = textRangeToDocRange(map, m.start, m.length);
    if (!range) return;
    decorations.push(Decoration.inline(range.from, range.to, {
      class: index === state.activeIndex ? 'search-match search-match-active' : 'search-match',
    }));
  });

  return DecorationSet.create(doc, decorations);
}

/**
 * 搜索高亮扩展。通过 `editor.commands.setSearchHighlight(...)` 更新，
 * 通过 `editor.commands.clearSearchHighlight()` 清除。
 */
export const SearchHighlight = Extension.create({
  name: SEARCH_HIGHLIGHT_KEY,

  addCommands() {
    return {
      setSearchHighlight: (state: SearchHighlightState) =>
        ({ tr, dispatch }: { tr: Transaction; dispatch?: (tr: Transaction) => void }) => {
          if (dispatch) tr.setMeta(pluginKey, state);
          return true;
        },
      clearSearchHighlight: () =>
        ({ tr, dispatch }: { tr: Transaction; dispatch?: (tr: Transaction) => void }) => {
          if (dispatch) {
            tr.setMeta(pluginKey, { query: '', matches: [], activeIndex: 0, caseSensitive: false, regex: false });
          }
          return true;
        },
    } as never;
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<SearchHighlightState>({
        key: pluginKey,
        state: {
          init: () => ({ query: '', matches: [], activeIndex: 0, caseSensitive: false, regex: false }),
          apply(tr, previous) {
            const meta = tr.getMeta(pluginKey) as SearchHighlightState | undefined;
            if (meta) return meta;
            // 文档变化后匹配已失效：清空高亮，由 UI 重新计算
            if (tr.docChanged && previous.matches.length > 0) {
              return { ...previous, matches: [], activeIndex: 0 };
            }
            return previous;
          },
        },
        props: {
          decorations(state) {
            return buildDecorations(state.doc, pluginKey.getState(state));
          },
        },
      }),
    ];
  },
});

/** 用匹配结果更新高亮 */
export function applySearchHighlight(editor: Editor, state: SearchHighlightState): void {
  (editor.commands as unknown as {
    setSearchHighlight: (s: SearchHighlightState) => boolean;
  }).setSearchHighlight(state);
}

/** 清除高亮 */
export function clearSearchHighlight(editor: Editor): void {
  (editor.commands as unknown as { clearSearchHighlight: () => boolean }).clearSearchHighlight();
}

/** 用纯文本替换文档区间（显式构造 text 节点，避免内容解析带来的长度语义差异） */
function replaceRangeWithText(editor: Editor, from: number, to: number, text: string): boolean {
  const { state, view } = editor;
  const tr = state.tr;
  if (text) {
    tr.replaceWith(from, to, state.schema.text(text));
  } else {
    tr.delete(from, to);
  }
  view.dispatch(tr);
  return true;
}

/**
 * 替换命中的文本。
 *
 * 实现要点：**每次替换后重新查找，并始终取第 `index` 个匹配**。
 * 因为替换会把第 index 个命中从文本中消除，下一次重新查找时
 * 同一个下标自然指向"原来的下一个"命中 —— 位置永远来自最新文档，
 * 不需要手工推导偏移位移。
 *
 * 反例（曾实测把「林渊看见林渊」改成「林李默见林李默」）：
 * 一次性按旧偏移替换多处，或手工累加长度差，都会因为每次插入
 * 改变其后文本的文档位置而落到错误位置。
 *
 * @param query 用于重新查找的查询串
 * @param options 匹配选项（大小写/正则），必须与产生 matches 时一致
 * @returns 实际替换次数（跨块边界的匹配会被跳过）
 */
export function replaceMatches(
  editor: Editor,
  query: string,
  options: { caseSensitive?: boolean; regex?: boolean } | undefined,
  replacement: string,
  all: boolean,
): number {
  let applied = 0;
  for (let index = 0; all || index === 0; index++) {
    const current = findMatches(editor.getText(), query, options).matches;
    if (current.length === 0) break;
    // 下标越界说明剩余命中不足（前一轮因跨块跳过），回到第一处继续
    const target = current[Math.min(index, current.length - 1)];
    const map = buildOffsetMap(editor.state.doc);
    const range = textRangeToDocRange(map, target.start, target.length);
    // 跨块无法替换：跳过并停止，避免死循环
    if (!range) break;
    if (!replaceRangeWithText(editor, range.from, range.to, replacement)) break;
    applied += 1;
  }
  return applied;
}

/** 滚动到指定匹配处（并选中它），供"下一个/上一个"使用 */
export function scrollToMatch(
  editor: Editor,
  match: { start: number; length: number },
): boolean {
  const map = buildOffsetMap(editor.state.doc);
  const range = textRangeToDocRange(map, match.start, match.length);
  if (!range) return false;
  editor.chain().focus().setTextSelection(range).scrollIntoView().run();
  return true;
}

/**
 * 伏笔引文定位：把"当初在编辑器里标记的那段文字"还原成当前文档的位置区间。
 *
 * 两级策略，因为两种信息各有失效场景：
 *
 * 1. **存下来的文档偏移**（标记时刻的快照）—— 精确，能区分同一句话的多次出现。
 *    但正文在其之前增删内容后会整体偏移。
 * 2. **文本搜索兜底** —— 只要那句话还在原文中就能找到，但句中有重复时只能选中第一处。
 *
 * 校验第 1 级时用 `doc.textBetween` 回读该区间，只有内容与引文完全一致才认它
 * （否则说明正文已改动，偏移不再指向这句话）。引文若跨段落，`textBetween` 返回的
 * 段落分隔是 `\n`，与实际存库的选区文本可能不同，此时直接落到第 2 级。
 *
 * @returns 命中的文档区间；引文已被删除（原文找不到）时返回 null
 */
export function findQuoteRange(
  doc: PMNode,
  quote: { text: string; from: number | null; to: number | null },
): { from: number; to: number } | null {
  const text = quote.text;
  if (!text.trim()) return null;
  const docSize = doc.content.size;

  if (
    typeof quote.from === 'number' && typeof quote.to === 'number'
    && quote.from >= 0 && quote.to > quote.from && quote.to <= docSize
  ) {
    try {
      if (doc.textBetween(quote.from, quote.to, '\n') === text) {
        return { from: quote.from, to: quote.to };
      }
    } catch {
      // 非法位置：交给文本搜索兜底，不要因为旧快照抛错而中断跳转
    }
  }

  // 兜底：在纯文本坐标里搜索，再换算回文档位置
  const { matches } = findMatches(doc.textBetween(0, docSize, BLOCK_SEPARATOR), text, {
    caseSensitive: true,
    regex: false,
  });
  if (matches.length === 0) return null;

  const map = buildOffsetMap(doc);
  // 同一句话出现多次时，优先取离原始标记位置最近的那处：
  // 正文改动只会让位置平移，不会跑出去很远，比"永远取第一处"更可能命中意图。
  const origin = typeof quote.from === 'number' ? quote.from : 0;
  const ordered = [...matches].sort(
    (a, b) => Math.abs(a.start - origin) - Math.abs(b.start - origin),
  );
  for (const m of ordered) {
    const range = textRangeToDocRange(map, m.start, m.length);
    if (range) return range;
  }
  return null;
}
